import { useTheme, alpha } from '@mui/material';
import { useMemo } from 'react';
import { useThemeManager } from './useThemeManager';
import { reefColors } from '../themes';

/**
 * Returns theme-aware style tokens for ImageLabStudio layout surfaces.
 * Centralizes the visual DNA so the studio component doesn't need conditionals.
 */
export function useStudioStyles() {
  const theme = useTheme();
  const { themeId } = useThemeManager();

  return useMemo(() => {
    const isReef = themeId === 'reef';
    const isBranded = themeId === 'smartai' || themeId === 'constellations';
    const isConstellations = themeId === 'constellations';
    const isDark = theme.palette.mode === 'dark';

    // Reef surfaces are opaque and drawn, not lit. The two branded dark themes
    // build panels out of translucent glass over a near-black ground; a sand-lit
    // identity has no glass to be translucent against, so reef gets its own
    // branch here rather than being folded into `isBranded` — which is exactly
    // what this hook exists for.
    const reefPanelBg = isDark ? reefColors.slate : reefColors.paper;
    const reefBorder = isDark ? reefColors.lineDark : reefColors.lineSoft;

    // Sidebar / panel background
    const panelBg = isReef
      ? reefPanelBg
      : isBranded && isDark
        ? isConstellations
          ? 'rgba(20, 27, 34, 0.88)'
          : 'rgba(30, 41, 59, 0.85)'
        : theme.palette.background.paper;

    // No blur anywhere in reef: the depth cue is a hairline, not a frosted pane.
    const panelBackdrop = isReef
      ? 'none'
      : isBranded && isDark
        ? isConstellations ? 'blur(16px)' : 'blur(12px)'
        : 'none';

    // Top toolbar strip. Reef's is the single sand-to-white (ink-to-paper) wash
    // the brand brief permits — two flat stops, no color mixing.
    const toolbarBg = (() => {
      // Flat, as drawn — the mockups give the lab top bar the paper surface
      // with a hairline under it, not a gradient.
      if (isReef) return isDark ? reefColors.ink2 : reefColors.paper;
      if (!isBranded || !isDark) return theme.palette.background.paper;
      if (isConstellations) {
        return `linear-gradient(135deg, rgba(7,227,247,0.08) 0%, rgba(29,158,225,0.06) 30%, rgba(60,55,193,0.04) 70%, rgba(20,27,34,0.95) 100%)`;
      }
      return `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha('#354dc7', 0.10)} 50%, ${alpha('#02f9fd', 0.05)} 100%), ${theme.palette.background.paper}`;
    })();

    // Category icon active indicator color. Reef spends its one coral here —
    // the active state is the single coral element of the rail.
    const categoryActiveIndicator = (() => {
      if (isReef) return isDark ? reefColors.terracottaLift : reefColors.terracottaInk;
      if (!isBranded) return theme.palette.primary.main;
      if (isConstellations) return isDark ? '#07E3F7' : '#1D9EE1';
      return isDark ? '#02f9fd' : theme.palette.primary.main;
    })();

    // Sidebar divider. Reef draws structure with a navy (or sand) hairline
    // rather than a glow.
    const panelBorder = (() => {
      if (isReef) return reefBorder;
      if (!isBranded || !isDark) return theme.palette.divider;
      if (isConstellations) return 'rgba(7, 227, 247, 0.06)';
      return 'rgba(2, 249, 253, 0.08)';
    })();

    // Right sidebar header wash.
    const sidebarHeaderBg = (primaryColor: string) => {
      if (isReef) {
        // Teal, not coral: the header sits above a panel that already spends
        // its coral on the primary action inside it.
        return isDark ? reefColors.terracottaWashDark : reefColors.terracottaWashLight;
      }
      if (isConstellations) {
        return `linear-gradient(135deg, rgba(7,227,247,0.10) 0%, rgba(60,55,193,0.06) 50%, rgba(64,41,189,0.03) 100%)`;
      }
      if (isBranded) {
        return `linear-gradient(135deg, ${alpha(primaryColor, 0.15)} 0%, ${alpha('#354dc7', 0.08)} 50%, ${alpha('#02f9fd', 0.05)} 100%)`;
      }
      return `linear-gradient(135deg, ${alpha(primaryColor, 0.1)} 0%, ${alpha(primaryColor, 0.05)} 100%)`;
    };

    // Whether to show constellation background in canvas area
    const showConstellationBg = isConstellations && isDark;

    // Whether to show the reef contour lattice in the canvas area. Unlike
    // Constellations this runs in BOTH modes: the motif is drawn geometry, not
    // a light effect, so it reads the same way on sand as on ink.
    const showReefBg = isReef;

    // Constellations replaces the canvas mat outright: its scene is an opaque
    // starfield, so a checkerboard under it would only be visible as noise.
    // Reef is deliberately NOT in this gate — its motif is a thin line drawing
    // that sits *above* the checker, so the transparency affordance survives.
    const showSceneBg = showConstellationBg;

    // The canvas mat behind the artwork, and the transparency checker drawn on
    // it. The studio's default checker is derived as `alpha(divider, 0.3)`,
    // which on a sand ground computes to about 1.1:1 — invisible as a
    // transparency cue and muddy as decoration. (MUI's `alpha()` *replaces* the
    // alpha channel of an rgba() input rather than multiplying it, so a
    // hairline divider does not scale down the way the call site assumes.)
    // Reef therefore names both explicitly; other themes keep the derivation.
    const canvasGround = isReef
      ? isDark
        ? reefColors.ink2
        : reefColors.stone2
      : showConstellationBg
        ? theme.palette.background.default
        // Everything else keeps what it had before this token existed: no mat
        // of its own, so the checkerboard's transparent squares show whatever
        // the studio's own surface is.
        : 'transparent';

    const canvasChecker = isReef
      ? isDark ? reefColors.canvasCheckerDark : reefColors.canvasCheckerLight
      : alpha(theme.palette.divider, 0.3);

    // Logo to show in toolbar (null = use default icon)
    //
    // The Coral mark needs no light/dark pair: it is alpha-keyed rather than
    // knocked out on a plate, and its cyan→purple body with orange polyps holds
    // contrast on both the warm stone and the ink. The two branded themes still
    // swap, because their marks ARE monochrome knockouts.
    const logoSrc = isReef
      ? '/images/coral-mark-512.png'
      : isBranded
        ? isDark ? '/images/logo-mark-white.svg' : '/images/logo-mark-main.svg'
        : null;

    // Travels with `logoSrc`, because the two branded themes still serve the
    // SmartAI mark: a static "Coral Creator" alt would have a screen reader
    // announce the wrong brand for the image actually on screen.
    const logoAlt = isReef ? 'Coral Creator' : 'SmartAI';

    return {
      panelBg,
      panelBackdrop,
      toolbarBg,
      categoryActiveIndicator,
      panelBorder,
      sidebarHeaderBg,
      showConstellationBg,
      showReefBg,
      showSceneBg,
      canvasGround,
      canvasChecker,
      logoSrc,
      logoAlt,
      isBranded,
      isConstellations,
      isReef,
    };
  }, [theme, themeId]);
}
